import React, { useEffect, useState } from "react";

import PdfCanvasPreview from "../teacher/components/PdfCanvasPreview";
import {
  MESSAGES_VERROU, annulerCopieEnAttente, copiesEnAttente, envoyerCopiesEnAttente, estGardeHorsConnexion,
  fichierHorsConnexion, finAccesHorsConnexion, garderHorsConnexion, listerHorsConnexion, retirerHorsConnexion,
  useChangementsHorsConnexion, useEnLigne, useVerrouHorsConnexion,
} from "../services/horsConnexion";
import { qlAlert } from "../utils/dialogs";

function taille(octets) {
  if (!octets) return "";
  return octets < 1024 * 1024 ? `${Math.max(1, Math.round(octets / 1024))} Ko` : `${(octets / 1024 / 1024).toFixed(1).replace(".", ",")} Mo`;
}

function dateHeure(valeur) {
  return new Date(valeur).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

// Adresse locale (blob:) du fichier gardé, ou null s'il n'est pas sur l'appareil.
export function useFichierHorsConnexion(cle) {
  const version = useChangementsHorsConnexion();
  const verrou = useVerrouHorsConnexion();
  const [fichier, setFichier] = useState(null);
  useEffect(() => {
    let annule = false;
    let adresse = null;
    fichierHorsConnexion(cle).then((entree) => {
      if (annule || !entree?.blob) { if (!annule) setFichier(null); return; }
      adresse = URL.createObjectURL(entree.blob);
      setFichier({ ...entree, adresse });
    });
    return () => {
      annule = true;
      if (adresse) URL.revokeObjectURL(adresse);
    };
  }, [cle, version, verrou]);
  return verrou ? null : fichier;
}

// « Garder hors connexion » / « Hors connexion ✓ » pour un fichier.
export function BoutonHorsConnexion({ cle, url, titre, type = "pdf", rubrique, details, compact = false, variante = "btn-outline-secondary", className = "" }) {
  const version = useChangementsHorsConnexion();
  const enLigne = useEnLigne();
  const [garde, setGarde] = useState(false);
  const [travail, setTravail] = useState(false);

  useEffect(() => {
    let annule = false;
    estGardeHorsConnexion(cle).then((oui) => { if (!annule) setGarde(oui); });
    return () => { annule = true; };
  }, [cle, version]);

  if (!url && !garde) return null;

  const basculer = async (event) => {
    event.stopPropagation();
    if (travail) return;
    setTravail(true);
    try {
      // Le bouton passe au vert (« Hors connexion ») : pas de fenêtre à fermer.
      if (garde) await retirerHorsConnexion(cle);
      else await garderHorsConnexion({ cle, url, titre, type, rubrique, details });
    } catch (erreur) {
      qlAlert(erreur?.message && !/fetch|network/i.test(erreur.message)
        ? erreur.message : "Téléchargement impossible. Vérifiez votre connexion.", "error");
    } finally {
      setTravail(false);
    }
  };

  const libelle = garde ? "Hors connexion" : "Garder hors connexion";
  return (
    <button type="button" onClick={basculer} disabled={travail || (!garde && !enLigne)}
      className={`btn btn-sm ${garde ? "btn-success" : variante} ${className}`.trim()}
      title={garde ? "Disponible sans Internet — cliquer pour retirer de l'appareil" : "Télécharger sur l'appareil pour le lire sans Internet"}
      aria-pressed={garde} aria-label={libelle}>
      {travail
        ? <span className="spinner-border spinner-border-sm" />
        : <i className={`bi ${garde ? "bi-cloud-check-fill" : "bi-cloud-arrow-down"}${compact ? "" : " me-1"}`}></i>}
      {!compact && libelle}
    </button>
  );
}

// PDF rendu sur l'appareil (pas besoin de réseau ni de visionneuse externe).
export function PdfLocal({ adresse, titre, plein = false, onClose }) {
  const contenu = <PdfCanvasPreview url={adresse} title={titre} className="ql-hc-pdf" />;
  if (!plein) return contenu;
  return (
    <div className="ql-hc-plein" role="dialog" aria-modal="true" aria-label={titre}>
      <div className="ql-hc-plein-barre">
        <span><i className="bi bi-cloud-check-fill me-2"></i>{titre}</span>
        <button type="button" className="btn btn-sm btn-light" onClick={onClose}>
          <i className="bi bi-x-lg me-1"></i>Fermer
        </button>
      </div>
      {contenu}
    </div>
  );
}

// Bandeau de l'espace élève quand le réseau manque.
export function BandeauHorsConnexion({ onVoir, generateur = false }) {
  const enLigne = useEnLigne();
  const verrou = useVerrouHorsConnexion();
  if (enLigne) return null;
  const fin = finAccesHorsConnexion();
  const valide = !verrou;
  return (
    <div className={`ql-hc-bandeau${valide ? "" : " ql-hc-bandeau--verrou"}`} role="status">
      <i className={`bi ${valide ? "bi-wifi-off" : "bi-lock-fill"}`}></i>
      <span>
        {valide
          ? <>Mode hors connexion : vous consultez les contenus gardés sur l'appareil{fin ? ` (jusqu'au ${dateHeure(fin)})` : ""}.{generateur ? " La génération et les paiements reprendront au retour du réseau." : " Vos copies partiront au retour du réseau."}</>
          : MESSAGES_VERROU[verrou]}
      </span>
      {valide && onVoir && <button type="button" className="btn btn-sm btn-light" onClick={onVoir}>Mes contenus</button>}
    </div>
  );
}

// Envoie les copies en attente au démarrage et au retour du réseau.
export function useEnvoiAutomatiqueDesCopies() {
  const enLigne = useEnLigne();
  useEffect(() => {
    if (!enLigne) return;
    envoyerCopiesEnAttente().then(({ envoyees, refusees }) => {
      if (envoyees.length) {
        qlAlert(envoyees.length === 1
          ? `Copie envoyée : ${envoyees[0].titre}.`
          : `${envoyees.length} copies préparées hors connexion ont été envoyées.`, "success");
      }
      if (refusees.length) qlAlert(`Copie non envoyée (${refusees[0].titre}) : ${refusees[0].erreur}`, "error");
    }).catch(() => {});
  }, [enLigne]);
}

const RUBRIQUES = {
  lecons: { titre: "Leçons", icone: "bi-file-earmark-text-fill" },
  formules: { titre: "Formules", icone: "bi-infinity" },
  devoirs: { titre: "Sujets de devoirs", icone: "bi-pencil-square" },
  evaluations: { titre: "Sujets d'évaluations", icone: "bi-clipboard-check" },
  audios: { titre: "Cours audio", icone: "bi-headphones" },
  epreuves: { titre: "Mes épreuves générées", icone: "bi-file-earmark-text" },
};

// Rubrique « Hors connexion » : ce qui est sur l'appareil et les copies en attente.
export function MesContenusHorsConnexion({ generateur = false, onBack }) {
  const version = useChangementsHorsConnexion();
  const enLigne = useEnLigne();
  const verrou = useVerrouHorsConnexion();
  const [fichiers, setFichiers] = useState(null);
  const [copies, setCopies] = useState([]);
  const [ouvert, setOuvert] = useState(null);
  const valide = !verrou;
  const fin = finAccesHorsConnexion();

  useEffect(() => {
    let annule = false;
    Promise.all([listerHorsConnexion(), copiesEnAttente()]).then(([f, c]) => {
      if (!annule) { setFichiers(f); setCopies(c); }
    });
    return () => { annule = true; };
  }, [version]);

  const fichierOuvert = useFichierHorsConnexion(ouvert?.cle);

  if (ouvert && fichierOuvert) {
    return (
      <div>
        <button type="button" className="btn btn-sm btn-outline-secondary mb-3" onClick={() => setOuvert(null)}>
          <i className="bi bi-arrow-left me-1"></i>Mes contenus hors connexion
        </button>
        <h5 className="fw-bold mb-3">{ouvert.titre}</h5>
        {ouvert.type === "audio"
          ? <audio controls src={fichierOuvert.adresse} className="w-100" />
          : <PdfLocal adresse={fichierOuvert.adresse} titre={ouvert.titre} />}
      </div>
    );
  }

  const groupes = Object.entries(RUBRIQUES)
    .map(([cle, r]) => ({ cle, ...r, elements: (fichiers || []).filter((f) => f.rubrique === cle) }))
    .filter((g) => g.elements.length);
  const total = (fichiers || []).reduce((somme, f) => somme + (f.taille || 0), 0);

  return (
    <div className="ql-hc-page">
      <div className="ql-view-header d-flex align-items-center gap-2">
        {onBack && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onBack}><i className="bi bi-arrow-left me-1"></i>Dashboard</button>}
        <h3 className="ql-view-title mb-0"><i className="bi bi-cloud-check me-2"></i>Hors connexion</h3>
      </div>
      <div className={`ql-hc-info${valide ? "" : " ql-hc-info--verrou"}`}>
        <i className={`bi ${valide ? "bi-shield-check" : "bi-lock-fill"}`}></i>
        <div>
          {valide
            ? <>Accès sans Internet valable jusqu'au <strong>{fin ? dateHeure(fin) : "—"}</strong>. Chaque connexion le prolonge de 48 h{generateur ? "" : " (dans la limite de votre abonnement)"}.</>
            : MESSAGES_VERROU[verrou]}
          <div className="small mt-1">
            {generateur
              ? <>Pour garder une épreuve, touchez <i className="bi bi-cloud-arrow-down"></i> à côté de son sujet ou de sa correction dans « Mes épreuves ».</>
              : <>Pour garder un contenu, touchez <i className="bi bi-cloud-arrow-down"></i> à côté d'une leçon, d'un sujet, d'une formule ou d'un cours audio.</>}
          </div>
        </div>
      </div>

      {copies.length > 0 && (
        <section className="mb-4">
          <h6 className="fw-bold mb-2"><i className="bi bi-send me-2"></i>Copies en attente d'envoi ({copies.length})</h6>
          {copies.map((c) => (
            <div key={c.id} className="ql-hc-ligne">
              <i className={`bi ${c.erreur ? "bi-exclamation-triangle-fill text-danger" : "bi-hourglass-split text-warning"}`}></i>
              <div className="flex-fill">
                <strong>{c.titre}</strong>
                <small>Préparée le {dateHeure(c.remiseLe)}{c.erreur ? ` — refusée : ${c.erreur}` : enLigne ? " — envoi en cours…" : " — partira au retour du réseau"}</small>
              </div>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => annulerCopieEnAttente(c.id)}>
                {c.erreur ? "Retirer" : "Annuler"}
              </button>
            </div>
          ))}
        </section>
      )}

      {fichiers === null ? (
        <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
      ) : !groupes.length ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-cloud-slash fs-1 d-block mb-2"></i>
          Aucun contenu gardé sur cet appareil pour le moment.
        </div>
      ) : (
        <>
          {groupes.map((g) => (
            <section key={g.cle} className="mb-4">
              <h6 className="fw-bold mb-2"><i className={`bi ${g.icone} me-2`}></i>{g.titre} ({g.elements.length})</h6>
              {g.elements.map((f) => (
                <div key={f.cle} className="ql-hc-ligne">
                  <i className={`bi ${f.type === "audio" ? "bi-headphones" : "bi-file-earmark-pdf-fill text-danger"}`}></i>
                  <div className="flex-fill">
                    <strong>{f.titre}</strong>
                    <small>{[f.details?.module, taille(f.taille), `gardé le ${dateHeure(f.date)}`].filter(Boolean).join(" · ")}</small>
                  </div>
                  <button type="button" className="btn btn-sm btn-primary" disabled={!valide} onClick={() => setOuvert(f)}>
                    <i className={`bi ${f.type === "audio" ? "bi-play-fill" : "bi-eye"} me-1`}></i>{f.type === "audio" ? "Écouter" : "Lire"}
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-danger" aria-label={`Retirer ${f.titre}`} onClick={() => retirerHorsConnexion(f.cle)}>
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              ))}
            </section>
          ))}
          <p className="text-muted small">Espace utilisé : {taille(total) || "0 Ko"}</p>
        </>
      )}
    </div>
  );
}
